/**
 * Where a clock record happened — SHOP or FIELD.
 *
 * Mirrors the API's `src/constants/work-locations.ts`, which is backed by the
 * CHECK constraint `timesheets_work_location_check` (migration 0333). Same
 * relationship this repo's `line-item-types.ts` has to the `line_item_type`
 * enum: a copy with no database behind it, so it can drift silently and the
 * test beside it is what stops that.
 *
 * ── NULL IS A THIRD STATE AND IT IS NOT 'field' ─────────────────────────────
 * The column is nullable. A shift recorded before this feature, or by a caller
 * that did not say, holds NULL — and NULL means *nobody said*, not "field".
 * Render it with `workLocationLabel()` below, which returns an em dash. The
 * whole silent-blank-field family is one rule: an absent value renders as `—`,
 * never as a plausible default.
 *
 * That is also why the picker has no default selection. Defaulting to 'field'
 * would write a location nobody chose onto a safety record, which is the same
 * mistake one layer earlier.
 */

export const WORK_LOCATIONS = ['shop', 'field'] as const;

export type WorkLocation = (typeof WORK_LOCATIONS)[number];

export const WORK_LOCATION_LABELS: Record<WorkLocation, string> = {
  shop: 'Shop',
  field: 'Field',
};

export function isWorkLocation(value: unknown): value is WorkLocation {
  return typeof value === 'string' && (WORK_LOCATIONS as readonly string[]).includes(value);
}

/** Display a stored value, including the absent case. Never returns ''. */
export function workLocationLabel(value: unknown): string {
  return isWorkLocation(value) ? WORK_LOCATION_LABELS[value] : '—';
}
